import { toastManager } from "../ui/toast";

const RECOVERY_WAITING_DESCRIPTION = "Waiting for CMMD to prepare this Environment.";
const RECOVERY_SUCCESS_DESCRIPTION =
  "CMMD confirmed identity, wake, Forge service readiness, and registration.";
const RECOVERY_SUCCESS_TIMEOUT_MS = 10_000;
const RECOVERY_ERROR_FALLBACK = "CMMD did not confirm a safe recovery result.";
const RECOVERING_ACTION_LABEL = "Recovering...";

export interface CloudRecoveryProgressHandlers {
  /** Sets or clears the row's busy action label (e.g. "Recovering..."). */
  readonly setBusyActionLabel: (label: string | null) => void;
  /** What to call the Environment in every state of the toast. */
  readonly environmentName: string;
}

/**
 * Runs a cloud environment recovery task behind a persistent loading toast.
 *
 * The task receives an `onWaiting` callback to report intermediate progress
 * (identity check, wake, Forge service readiness, registration); each call
 * updates the same toast in place rather than stacking new ones. The toast
 * resolves to a success or error terminal state depending on whether the
 * task throws. The busy action label is set for the duration of the task and
 * always cleared when it settles, regardless of outcome.
 */
export async function runCloudRecoveryWithProgressToast(
  task: (onWaiting: (message: string) => void) => Promise<void>,
  handlers: CloudRecoveryProgressHandlers,
): Promise<void> {
  handlers.setBusyActionLabel(RECOVERING_ACTION_LABEL);
  const progressTitle = `Recovering ${handlers.environmentName}`;
  const progressToastId = toastManager.add({
    type: "loading",
    title: progressTitle,
    description: RECOVERY_WAITING_DESCRIPTION,
    timeout: 0,
  });
  try {
    await task((message) => {
      toastManager.update(progressToastId, {
        type: "loading",
        title: progressTitle,
        description: message,
        timeout: 0,
      });
    });
    toastManager.update(progressToastId, {
      type: "success",
      title: `Recovery complete for ${handlers.environmentName}`,
      description: RECOVERY_SUCCESS_DESCRIPTION,
      timeout: RECOVERY_SUCCESS_TIMEOUT_MS,
    });
  } catch (error) {
    toastManager.update(progressToastId, {
      type: "error",
      title: `Recovery stopped for ${handlers.environmentName}`,
      description: error instanceof Error ? error.message : RECOVERY_ERROR_FALLBACK,
      timeout: 0,
    });
  } finally {
    handlers.setBusyActionLabel(null);
  }
}
